/** What each element's attribute held before we wrote it, so a restore is exact. */
export type Ledger = Map<Element, Map<string, string | null>>;

/* Records the previous value once — a second write of the same attribute must not overwrite
   the value the restore has to put back. `null` removes the attribute. */
export const write = (
	ledger: Ledger,
	element: Element,
	name: string,
	value: string | null,
): void => {
	let owned = ledger.get(element);

	if (!owned) {
		owned = new Map();
		ledger.set(element, owned);
	}

	if (!owned.has(name)) owned.set(name, element.getAttribute(name));

	if (value === null) element.removeAttribute(name);
	else element.setAttribute(name, value);
};

export const giveBack = (ledger: Ledger): void => {
	for (const [element, owned] of ledger) {
		for (const [name, value] of owned) {
			if (value === null) element.removeAttribute(name);
			else element.setAttribute(name, value);
		}
	}

	ledger.clear();
};

/* Every sibling of every ancestor, not only the body's children: the vendor's template may
   nest the header in a wrapper, and then the body has no other child left to neutralise. */
const neutralise = (ledger: Ledger, keep: readonly Element[]): void => {
	const kept = keep.filter((element) => element.isConnected);
	const holds = (element: Element) => kept.some((one) => element.contains(one));

	for (const start of kept) {
		let node: Element = start;

		while (node.parentElement && node !== document.body) {
			for (const sibling of node.parentElement.children)
				if (sibling !== node && !holds(sibling)) write(ledger, sibling, "inert", "");

			node = node.parentElement;
		}
	}
};

type Claim = { keep: readonly Element[] };
type Isolation = { ledger: Ledger; claims: Claim[] };

/* One ledger per header: with a ledger each, the second records the first's `inert` as the
   value to restore and the page stays inert for good. */
const isolations = new WeakMap<Element, Isolation>();

/* Restore before re-walking: the ledger holds true previous values only in that state. */
const reapply = (isolation: Isolation): void => {
	giveBack(isolation.ledger);

	const top = isolation.claims.at(-1);

	if (top) neutralise(isolation.ledger, top.keep);
};

/** Makes everything outside `keep` inert and returns its disposer. The newest claim governs. */
export const isolate = (root: HTMLElement, keep: readonly Element[] = [root]): (() => void) => {
	let isolation = isolations.get(root);

	if (!isolation) {
		const fresh: Isolation = { ledger: new Map(), claims: [] };

		isolations.set(root, fresh);
		isolation = fresh;
	}

	const held = isolation;

	/* A fresh object per call, so a caller hoisting its `keep` array cannot splice a peer's. */
	const claim: Claim = { keep };

	held.claims.push(claim);
	reapply(held);

	let released = false;

	return () => {
		if (released) return;

		released = true;

		const index = held.claims.indexOf(claim);

		if (index !== -1) held.claims.splice(index, 1);

		reapply(held);
	};
};

export type PageHold = { release?: () => void; unisolate?: () => void };

export const freePage = (hold: PageHold): void => {
	hold.unisolate?.();
	hold.unisolate = undefined;

	hold.release?.();
	hold.release = undefined;
};
